import { ACKNOWLEDGEMENT_LOOKBACK_MS } from "@storyboard/dispatch-view/acknowledgement";
import { formatChipTime } from "@/components/feed/tmsEvent/TmsEventChip";
import { tmsEventChipText } from "@/components/feed/tmsEvent/tmsEventDisplay";
import type { DriverVolatileRow } from "@/models/repositories/Driver";
import { idx, store } from "@/store/store";
import type { TmsEventRow } from "@/store/types";
import type { AffectedKeys } from "@/store/useKeyedCompute";
import { type DriverRecentSource, PHOTO_MESSAGE_TEXT } from "@/types/driver";
import { type AcknowledgementRequirement, requiredTmsEvents, tmsEventArrivedAt } from "./acknowledgements";

/**
 * What a driver's row reads about the driver's traffic: whether any feed is unread, the newest thing on them, and
 * whether that newest thing is a message or a required TMS event.
 */
export type DriverActivity = Pick<DriverVolatileRow, "unread" | "latestItemCreatedAt" | "recentMessage"> & {
  /** Null when the row shows nothing. */
  recentSource: DriverRecentSource | null;
};

/** A driver's newest required TMS event, on one alias feed of the driver's, as the driver's row shows it. */
export type DriverEvent = {
  /** The alias feed the event is shown on. */
  feedId: string;
  driverMembershipId: string;
  /** When the event reached the server, as `tmsEventArrivedAt` gives it. */
  createdAt: string;
  /** The event's chip text, as its thread chip and message row show it. */
  text: string;
};

/** The collections {@link newestDriverEvent} reads, for `useKeyedCompute`. */
export const driverEventCollections = ["feed", "tmsEvent"] as const;

/** Which alias feeds' newest event a written row can change, for `useKeyedCompute` over {@link driverEventCollections}. */
export const driverEventFeedsAffected: AffectedKeys = {
  feed: (feed) => [feed.id],
  tmsEvent: (event) => idx.feedsByAliasMembership.keys(event.attributedMembershipId),
};

/**
 * An alias feed's newest TMS event of the kinds `requires` takes, within the acknowledgement lookback; null when it has
 * none, or is not a driver's alias feed.
 *
 * Reads the events the way the message-grain rows and the red dot do (`requiredTmsEvents`, `tmsEventArrivedAt`, the
 * chip text), so the three agree. It never walks messages or folds acknowledgements, since the row needs neither.
 * Which feeds the member may read is the roster's call; this answers for any feed it is handed.
 */
export function newestDriverEvent(
  feedId: string,
  requires: AcknowledgementRequirement,
  now: number,
): DriverEvent | null {
  const feed = store.get("feed", feedId);
  if (!feed?.isAliasFeed || !feed.aliasMembershipId) return null;

  // Keep the newest required event inside the lookback; anything older is handled by age and never a row.
  const oldest = now - ACKNOWLEDGEMENT_LOOKBACK_MS;
  let newest: TmsEventRow | null = null;
  let newestAt = 0;
  for (const event of requiredTmsEvents(feed.aliasMembershipId, requires)) {
    const arrivedAt = Date.parse(tmsEventArrivedAt(event));
    if (arrivedAt < oldest) continue;
    if (newest !== null && arrivedAt <= newestAt) continue;
    newest = event;
    newestAt = arrivedAt;
  }
  if (newest === null) return null;
  return {
    feedId,
    driverMembershipId: feed.aliasMembershipId,
    createdAt: tmsEventArrivedAt(newest),
    text: tmsEventChipText(newest, formatChipTime),
  };
}

/** What a feed's latest message shows on the driver's row: a photo is described, never named by its file. */
function recentOf(row: DriverVolatileRow): Pick<DriverActivity, "recentMessage" | "recentSource"> {
  if (!row.latestItemCreatedAt) return { recentMessage: row.recentMessage, recentSource: null };
  if (row.recentMessagePhoto) return { recentMessage: PHOTO_MESSAGE_TEXT, recentSource: "photo" };
  return { recentMessage: row.recentMessage, recentSource: "message" };
}

/**
 * Folds a driver's alias feeds and required TMS events into one activity summary per driver.
 *
 * Unread when any feed is. The latest item and its message are the newest of the feeds' latest own messages and the
 * driver's `events`, so a required event that arrives after the driver's last message bumps the row the way a message
 * would. An event never makes a row unread, and on an equal instant the message wins. A photo with no transcription
 * reads as {@link PHOTO_MESSAGE_TEXT}, never its generated file name.
 *
 * @param rows - The driver compute's per-feed rows, already narrowed to the team's channels when a team is in force.
 * @param events - The driver's newest required TMS events, on the same feeds as `rows`.
 */
export function activityByDriver(
  rows: readonly DriverVolatileRow[],
  events: readonly DriverEvent[],
): Map<string, DriverActivity> {
  const byDriver = new Map<string, DriverActivity>();
  for (const row of rows) {
    if (!row.aliasMembershipId) continue;
    const existing = byDriver.get(row.aliasMembershipId);
    if (!existing) {
      byDriver.set(row.aliasMembershipId, {
        unread: row.unread ?? false,
        latestItemCreatedAt: row.latestItemCreatedAt,
        ...recentOf(row),
      });
      continue;
    }
    existing.unread = existing.unread || (row.unread ?? false);
    if (
      row.latestItemCreatedAt &&
      (!existing.latestItemCreatedAt || row.latestItemCreatedAt > existing.latestItemCreatedAt)
    ) {
      existing.latestItemCreatedAt = row.latestItemCreatedAt;
      Object.assign(existing, recentOf(row));
    }
  }

  // Events bump a row only when strictly newer, compared as instants: an event's time and a message's may be
  // formatted differently.
  for (const event of events) {
    const existing = byDriver.get(event.driverMembershipId);
    if (!existing) {
      byDriver.set(event.driverMembershipId, {
        unread: false,
        latestItemCreatedAt: event.createdAt,
        recentMessage: event.text,
        recentSource: "tmsEvent",
      });
      continue;
    }
    if (!existing.latestItemCreatedAt || Date.parse(event.createdAt) > Date.parse(existing.latestItemCreatedAt)) {
      existing.latestItemCreatedAt = event.createdAt;
      existing.recentMessage = event.text;
      existing.recentSource = "tmsEvent";
    }
  }
  return byDriver;
}
